import { useCallback, useEffect, useRef, useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  BackgroundVariant,
} from 'reactflow';
import 'reactflow/dist/style.css';

import useFlowStore from '../stores/useFlowStore';
import TableNode from './nodes/TableNode';
import CollectionNode from './nodes/CollectionNode';
import FactNode from './nodes/FactNode';
import DimensionNode from './nodes/DimensionNode';
import ViewNode from './nodes/ViewNode';
import TextNode from './nodes/TextNode';
import CrowfootEdge from './edges/CrowfootEdge';
import CardinalityModal from './modals/CardinalityModal';
import { SNAP_GRID, NODE_COLLISION_OPTIONS } from '../utils/constants';
import { resolveCollisions } from '../utils/resolveCollisions';
import { isNodeMatchingFilter } from '../utils/helpers';

const nodeTypes = {
  table: TableNode,
  collection: CollectionNode,
  fact: FactNode,
  dimension: DimensionNode,
  view: ViewNode,
  text: TextNode,
};

const edgeTypes = {
  default: CrowfootEdge,
  foreignKey: CrowfootEdge,
  reference: CrowfootEdge,
  dimension: CrowfootEdge,
  embedded: CrowfootEdge,
};

const Canvas = () => {
  const {
    nodes,
    edges,
    viewport,
    theme,
    isMultiSelectMode,
    modelType,
    onNodesChange,
    onEdgesChange,
    onConnect,
    setSelectedNode,
    setSelectedEdge,
    clearSelection,
    setViewport,
    setEdges,
    setNodes,
    pendingConnection,
    confirmConnection,
    cancelConnection,
  } = useFlowStore();

  const onNodeClick = useCallback(
    (_, node) => {
      setSelectedNode(node);
    },
    [setSelectedNode]
  );

  const onEdgeClick = useCallback(
    (_, edge) => {
      setSelectedEdge(edge);
    },
    [setSelectedEdge]
  );

  const onPaneClick = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  const onNodeDragStop = useCallback(() => {
    const current = useFlowStore.getState().nodes;
    const resolved = resolveCollisions(current, NODE_COLLISION_OPTIONS);
    setNodes(resolved);
  }, [setNodes]);

  const onViewportChange = useCallback(
    (newViewport) => {
      setViewport(newViewport);
    },
    [setViewport]
  );

  // Keep track of previous edges to avoid infinite loops
  const prevEdgesRef = useRef(null);

  // Auto-distribute connections to different target and source handles (stable order by edge id)
  useEffect(() => {
    const edgesKey = edges
      .map((e) => `${e.id}-${e.source}-${e.sourceHandle}-${e.target}-${e.targetHandle}`)
      .join('|');

    if (prevEdgesRef.current === edgesKey) {
      return;
    }

    const edgesByTarget = {};
    const edgesBySource = {};
    edges.forEach((edge) => {
      if (!edgesByTarget[edge.target]) edgesByTarget[edge.target] = [];
      edgesByTarget[edge.target].push(edge);
      if (!edgesBySource[edge.source]) edgesBySource[edge.source] = [];
      edgesBySource[edge.source].push(edge);
    });
    Object.values(edgesByTarget).forEach((arr) =>
      arr.sort((a, b) => a.id.localeCompare(b.id))
    );
    Object.values(edgesBySource).forEach((arr) =>
      arr.sort((a, b) => a.id.localeCompare(b.id))
    );

    let needsUpdate = false;
    const updatedEdges = edges.map((edge) => {
      const targetEdges = edgesByTarget[edge.target] || [];
      const sourceEdges = edgesBySource[edge.source] || [];
      const targetIndex = targetEdges.findIndex((e) => e.id === edge.id);
      const sourceIndex = sourceEdges.findIndex((e) => e.id === edge.id);
      const expectedTargetHandle = `target-${targetIndex >= 0 ? targetIndex : 0}`;
      const expectedSourceHandle = `source-${sourceIndex >= 0 ? sourceIndex : 0}`;

      if (
        edge.targetHandle !== expectedTargetHandle ||
        edge.sourceHandle !== expectedSourceHandle
      ) {
        needsUpdate = true;
        return {
          ...edge,
          targetHandle: expectedTargetHandle,
          sourceHandle: expectedSourceHandle,
        };
      }
      return edge;
    });

    if (needsUpdate) {
      prevEdgesRef.current = updatedEdges
        .map((e) => `${e.id}-${e.source}-${e.sourceHandle}-${e.target}-${e.targetHandle}`)
        .join('|');
      setEdges(updatedEdges);
    } else {
      prevEdgesRef.current = edgesKey;
    }
  }, [edges, setEdges]);

  const diagramFilter = useFlowStore((state) => state.diagramFilter);

  const displayNodes = useMemo(() => {
    if (!diagramFilter || !diagramFilter.active) {
      return nodes;
    }

    return nodes.map((node) => {
      const matches = isNodeMatchingFilter(node, diagramFilter);
      if (matches) {
        return {
          ...node,
          hidden: false,
          style: { ...node.style, opacity: 1 }
        };
      } else {
        if (diagramFilter.hideUnmatched) {
          return { ...node, hidden: true };
        } else {
          return {
            ...node,
            hidden: false,
            style: { ...node.style, opacity: 0.15, filter: 'grayscale(80%)' }
          };
        }
      }
    });
  }, [nodes, diagramFilter]);

  const displayEdges = useMemo(() => {
    if (!diagramFilter || !diagramFilter.active) {
      return edges;
    }

    const visibleNodeIds = new Set(
      displayNodes
        .filter((n) => !n.hidden && (n.style?.opacity ?? 1) > 0.3)
        .map((n) => n.id)
    );

    return edges.map((edge) => {
      const sourceVisible = visibleNodeIds.has(edge.source);
      const targetVisible = visibleNodeIds.has(edge.target);

      if (sourceVisible && targetVisible) {
        return { ...edge, hidden: false, style: { ...edge.style, opacity: 1 } };
      } else if (diagramFilter.hideUnmatched) {
        return { ...edge, hidden: true };
      } else {
        return { ...edge, hidden: false, style: { ...edge.style, opacity: 0.15 } };
      }
    });
  }, [edges, displayNodes, diagramFilter]);

  return (
    <div className="flex-1 h-full">
      <ReactFlow
        nodes={displayNodes}
        edges={displayEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        onPaneClick={onPaneClick}
        onNodeDragStop={onNodeDragStop}
        onViewportChange={onViewportChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultViewport={viewport}
        snapToGrid={true}
        snapGrid={SNAP_GRID}
        attributionPosition="bottom-left"
        fitView
        minZoom={0.1}
        maxZoom={2}
        className={theme === 'dark' ? 'dark' : ''}
        multiSelectionKeyCode="Control"
        selectionMode={isMultiSelectMode ? "partial" : undefined}
        panOnDrag={isMultiSelectMode ? [1, 2] : true}
        selectionOnDrag={isMultiSelectMode}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={15}
          size={1}
          className={theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}
        />
        <Controls
          className={theme === 'dark' ? 'dark' : ''}
          showInteractive={false}
        />
        <MiniMap
          nodeColor={(node) => {
            // If node has a custom color from tags, use it
            if (node.data?.color) {
              return node.data.color;
            }
            
            // Otherwise, use default colors based on type
            const colors = {
              table: '#3b82f6',
              collection: '#10b981',
              fact: '#f59e0b',
              dimension: '#8b5cf6',
              view: '#06b6d4',
              text: '#fef9c3',
            };
            return colors[node.type] || '#6b7280';
          }}
          className={theme === 'dark' ? 'dark' : ''}
          maskColor={theme === 'dark' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)'}
        />
      </ReactFlow>
      
      {/* Cardinality Selection Modal */}
      {pendingConnection && (
        <CardinalityModal
          onClose={cancelConnection}
          onConfirm={confirmConnection}
          sourceNode={pendingConnection.sourceNode}
          targetNode={pendingConnection.targetNode}
          modelType={modelType}
        />
      )}
    </div>
  );
};

export default Canvas;
